import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, ArrowLeft } from 'lucide-react';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';

const DivisionsPage = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();

  const allDivisions = [
    {
      icon: 'wazin-icon',
      titleAr: 'دائرة التقاضي',
      titleEn: 'Litigation Division',
      descriptionAr: 'تمثيل قانوني متكامل أمام المحاكم بمختلف درجاتها، من المحاكم الابتدائية إلى المحكمة العليا. نقدم خدمات التقاضي في جميع أنواع القضايا المدنية والتجارية والإدارية.',
      descriptionEn: 'Comprehensive legal representation in courts of all levels, from primary courts to the Supreme Court. We provide litigation services in all types of civil, commercial and administrative cases.',
      link: '/divisions/litigation',
      features: [
        i18n.language === 'ar' ? 'تمثيل أمام المحاكم' : 'Court representation',
        i18n.language === 'ar' ? 'إعداد المذكرات القانونية' : 'Legal memoranda preparation',
        i18n.language === 'ar' ? 'الطعن بالأحكام' : 'Appeal of judgments',
        i18n.language === 'ar' ? 'التنفيذ القضائي' : 'Judicial enforcement'
      ]
    },
    {
      icon: '📝',
      titleAr: 'دائرة العقود والشركات',
      titleEn: 'Contracts & Corporate Division',
      descriptionAr: 'صياغة ومراجعة جميع أنواع العقود التجارية والمدنية، وتقديم الاستشارات القانونية المتعلقة بتأسيس الشركات وإدارتها وحوكمتها.',
      descriptionEn: 'Drafting and reviewing all types of commercial and civil contracts, and providing legal advice related to company formation, management and governance.',
      link: '/divisions/contracts-and-corporate',
      features: [
        i18n.language === 'ar' ? 'صياغة العقود' : 'Contract drafting',
        i18n.language === 'ar' ? 'تأسيس الشركات' : 'Company formation',
        i18n.language === 'ar' ? 'الحوكمة المؤسسية' : 'Corporate governance',
        i18n.language === 'ar' ? 'عقود الاندماج والاستحواذ' : 'M&A contracts'
      ]
    },
    {
      icon: '💼',
      titleAr: 'دائرة الدراسات والاستشارات',
      titleEn: 'Studies & Consultations Division',
      descriptionAr: 'تقديم استشارات قانونية متخصصة في جميع المجالات، وإعداد الدراسات القانونية والبحوث المتعمقة للقضايا المعقدة.',
      descriptionEn: 'Providing specialized legal consultations in all fields, and preparing legal studies and in-depth research for complex issues.',
      link: '/divisions/consultations',
      features: [
        i18n.language === 'ar' ? 'استشارات قانونية' : 'Legal consultations',
        i18n.language === 'ar' ? 'الدراسات القانونية' : 'Legal studies',
        i18n.language === 'ar' ? 'البحوث المتخصصة' : 'Specialized research',
        i18n.language === 'ar' ? 'الآراء القانونية' : 'Legal opinions'
      ]
    },
    {
      icon: '🗂️',
      titleAr: 'دائرة التوثيق',
      titleEn: 'Notary Division',
      descriptionAr: 'خدمات التوثيق المعتمدة للعقود والمستندات القانونية وفقاً للأنظمة واللوائح المعمول بها في المملكة.',
      descriptionEn: 'Certified notary services for contracts and legal documents in accordance with the regulations in force in the Kingdom.',
      link: '/divisions/notary',
      features: [
        i18n.language === 'ar' ? 'توثيق العقود' : 'Contract notarization',
        i18n.language === 'ar' ? 'التصديقات القانونية' : 'Legal certifications',
        i18n.language === 'ar' ? 'الوكالات الرسمية' : 'Official agencies',
        i18n.language === 'ar' ? 'الإقرارات القانونية' : 'Legal declarations'
      ]
    },
    {
      icon: '🔐',
      titleAr: 'دائرة الجرائم المعلوماتية',
      titleEn: 'Cyber Crimes Division',
      descriptionAr: 'متخصصون في معالجة قضايا الجرائم الإلكترونية والمعلوماتية، وتقديم الدعم القانوني الكامل للمتضررين من الجرائم الرقمية.',
      descriptionEn: 'Specialists in handling cybercrime and information crimes cases, providing full legal support to victims of digital crimes.',
      link: '/divisions/cyber-crimes',
      features: [
        i18n.language === 'ar' ? 'جرائم الاحتيال الإلكتروني' : 'Electronic fraud crimes',
        i18n.language === 'ar' ? 'الابتزاز الرقمي' : 'Digital blackmail',
        i18n.language === 'ar' ? 'انتهاك الخصوصية' : 'Privacy violations',
        i18n.language === 'ar' ? 'القرصنة الإلكترونية' : 'Electronic piracy'
      ]
    },
    {
      icon: '🧩',
      titleAr: 'دائرة الامتثال والحوكمة',
      titleEn: 'Compliance & Governance Division',
      descriptionAr: 'بناء وتطوير أنظمة الامتثال والحوكمة للمؤسسات، وضمان التوافق مع المتطلبات التنظيمية والقانونية المحلية والدولية.',
      descriptionEn: 'Building and developing compliance and governance systems for institutions, ensuring compliance with local and international regulatory and legal requirements.',
      link: '/divisions/compliance',
      features: [
        i18n.language === 'ar' ? 'برامج الامتثال' : 'Compliance programs',
        i18n.language === 'ar' ? 'الحوكمة المؤسسية' : 'Corporate governance',
        i18n.language === 'ar' ? 'إدارة المخاطر' : 'Risk management',
        i18n.language === 'ar' ? 'مكافحة غسل الأموال' : 'Anti-money laundering'
      ]
    },
    {
      icon: '🏛️',
      titleAr: 'دائرة القانون الإداري',
      titleEn: 'Administrative Law Division',
      descriptionAr: 'التعامل مع القضايا الإدارية والطعون ضد القرارات الحكومية، وتمثيل العملاء أمام الجهات الإدارية.',
      descriptionEn: 'Handling administrative cases and appeals against government decisions, representing clients before administrative authorities.',
      link: '/divisions/administrative-law',
      features: [
        i18n.language === 'ar' ? 'الطعون الإدارية' : 'Administrative appeals',
        i18n.language === 'ar' ? 'القرارات الحكومية' : 'Government decisions',
        i18n.language === 'ar' ? 'العقود الإدارية' : 'Administrative contracts',
        i18n.language === 'ar' ? 'التحكيم الإداري' : 'Administrative arbitration'
      ]
    },
    {
      icon: '⚡',
      titleAr: 'دائرة التحكيم التجاري',
      titleEn: 'Commercial Arbitration Division',
      descriptionAr: 'تقديم خدمات التحكيم التجاري المحلي والدولي، وتمثيل العملاء في إجراءات التحكيم أمام مختلف المراكز.',
      descriptionEn: 'Providing local and international commercial arbitration services, representing clients in arbitration proceedings before various centers.',
      link: '/divisions/arbitration',
      features: [
        i18n.language === 'ar' ? 'التحكيم المحلي' : 'Local arbitration',
        i18n.language === 'ar' ? 'التحكيم الدولي' : 'International arbitration',
        i18n.language === 'ar' ? 'صياغة شروط التحكيم' : 'Arbitration clause drafting',
        i18n.language === 'ar' ? 'تنفيذ أحكام التحكيم' : 'Arbitration award enforcement'
      ]
    },
    {
      icon: '🏢',
      titleAr: 'دائرة العقارات',
      titleEn: 'Real Estate Division',
      descriptionAr: 'الخدمات القانونية المتعلقة بالمعاملات العقارية، من البيع والشراء إلى التأجير والتطوير العقاري.',
      descriptionEn: 'Legal services related to real estate transactions, from buying and selling to leasing and real estate development.',
      link: '/divisions/real-estate',
      features: [
        i18n.language === 'ar' ? 'عقود البيع والشراء' : 'Sale and purchase contracts',
        i18n.language === 'ar' ? 'التأجير التجاري' : 'Commercial leasing',
        i18n.language === 'ar' ? 'التطوير العقاري' : 'Real estate development',
        i18n.language === 'ar' ? 'النزاعات العقارية' : 'Real estate disputes'
      ]
    },
    {
      icon: '👥',
      titleAr: 'دائرة قانون العمل',
      titleEn: 'Labor Law Division',
      descriptionAr: 'تقديم الاستشارات القانونية وتمثيل العملاء في قضايا العمل والموارد البشرية وفقاً لنظام العمل السعودي.',
      descriptionEn: 'Providing legal advice and representing clients in labor and human resources cases according to Saudi labor law.',
      link: '/divisions/labor-law',
      features: [
        i18n.language === 'ar' ? 'عقود العمل' : 'Employment contracts',
        i18n.language === 'ar' ? 'النزاعات العمالية' : 'Labor disputes',
        i18n.language === 'ar' ? 'الفصل التعسفي' : 'Wrongful termination',
        i18n.language === 'ar' ? 'التأمينات الاجتماعية' : 'Social insurance'
      ]
    },
    {
      icon: '🔬',
      titleAr: 'دائرة الملكية الفكرية',
      titleEn: 'Intellectual Property Division',
      descriptionAr: 'حماية حقوق الملكية الفكرية من علامات تجارية وبراءات اختراع وحقوق المؤلف والأسرار التجارية.',
      descriptionEn: 'Protecting intellectual property rights including trademarks, patents, copyrights and trade secrets.',
      link: '/divisions/intellectual-property',
      features: [
        i18n.language === 'ar' ? 'تسجيل العلامات التجارية' : 'Trademark registration',
        i18n.language === 'ar' ? 'براءات الاختراع' : 'Patents',
        i18n.language === 'ar' ? 'حقوق المؤلف' : 'Copyrights',
        i18n.language === 'ar' ? 'مكافحة التقليد' : 'Anti-counterfeiting'
      ]
    },
    {
      icon: '⚖️',
      titleAr: 'دائرة الأحوال الشخصية',
      titleEn: 'Personal Status Division',
      descriptionAr: 'التعامل مع قضايا الأحوال الشخصية والأسرية، من الزواج والطلاق إلى الميراث والوصية.',
      descriptionEn: 'Handling personal status and family cases, from marriage and divorce to inheritance and wills.',
      link: '/divisions/personal-status',
      features: [
        i18n.language === 'ar' ? 'قضايا الأسرة' : 'Family cases',
        i18n.language === 'ar' ? 'الميراث والوصايا' : 'Inheritance and wills',
        i18n.language === 'ar' ? 'الحضانة والنفقة' : 'Custody and alimony',
        i18n.language === 'ar' ? 'توثيق الزواج' : 'Marriage documentation'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <Navbar />
      
      {/* Page Header */}
      <section className="pt-40 pb-16 px-4 bg-gradient-to-r from-wazin-navy to-wazin-navy/90">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-5xl sm:text-6xl font-bold text-white mb-6">
            {i18n.language === 'ar' ? 'الدوائر القانونية' : 'Legal Divisions'}
          </h1>
          <p className="text-xl text-white/90 max-w-3xl mx-auto mb-8">
            {i18n.language === 'ar' 
              ? 'دوائر متخصصة تغطي جميع احتياجاتكم القانونية بدقة واحترافية عالية'
              : 'Specialized divisions covering all your legal needs with precision and high professionalism'
            }
          </p>
          
          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto mt-12">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
              <p className="text-4xl font-bold text-wazin-gold mb-2">12</p>
              <p className="text-white/80 text-sm">{i18n.language === 'ar' ? 'دائرة متخصصة' : 'Specialized Divisions'}</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
              <p className="text-4xl font-bold text-wazin-gold mb-2">+50</p>
              <p className="text-white/80 text-sm">{i18n.language === 'ar' ? 'خبير قانوني' : 'Legal Experts'}</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
              <p className="text-4xl font-bold text-wazin-gold mb-2">+1000</p>
              <p className="text-white/80 text-sm">{i18n.language === 'ar' ? 'قضية منجزة' : 'Completed Cases'}</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
              <p className="text-4xl font-bold text-wazin-gold mb-2">24/7</p>
              <p className="text-white/80 text-sm">{i18n.language === 'ar' ? 'دعم متواصل' : 'Continuous Support'}</p>
            </div>
          </div>
        </div>
      </section>

      {/* All Divisions Grid */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {allDivisions.map((division, index) => (
              <div
                key={index}
                onClick={() => navigate(division.link)}
                className="group bg-white p-8 rounded-2xl shadow-md hover:shadow-2xl transition-all duration-300 cursor-pointer transform hover:-translate-y-2 border border-gray-100"
                style={{ animationDelay: `${index * 0.05}s` }}
              >
                {/* Icon Circle */}
                <div className="w-20 h-20 rounded-full flex items-center justify-center mb-6 bg-gradient-to-br from-wazin-gold to-wazin-navy group-hover:scale-110 transition-transform duration-300 shadow-lg">
                  {division.icon === 'wazin-icon' ? (
                    <img 
                      src="/wazin-icon-gold.png" 
                      alt="Wazin Icon" 
                      className="w-12 h-12 object-contain"
                    />
                  ) : (
                    <span className="text-4xl">{division.icon}</span>
                  )}
                </div>

                {/* Title */}
                <h3 className="text-2xl font-bold mb-4 text-wazin-navy group-hover:text-wazin-gold transition-colors duration-300">
                  {i18n.language === 'ar' ? division.titleAr : division.titleEn}
                </h3>

                {/* Description */}
                <p className="text-gray-600 leading-relaxed mb-6 text-sm">
                  {i18n.language === 'ar' ? division.descriptionAr : division.descriptionEn}
                </p>

                {/* Features */}
                <ul className="space-y-2 mb-4">
                  {division.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-sm text-gray-700">
                      <span className="w-1.5 h-1.5 rounded-full bg-wazin-gold"></span>
                      {feature}
                    </li>
                  ))}
                </ul>

                {/* Hover Arrow */}
                <div className="mt-4 flex items-center gap-2 text-wazin-gold opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <span className="text-sm font-semibold">
                    {i18n.language === 'ar' ? 'المزيد' : 'Learn More'}
                  </span>
                  {i18n.language === 'ar' ? (
                    <ArrowLeft className="w-4 h-4" />
                  ) : (
                    <ArrowRight className="w-4 h-4" />
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-r from-wazin-navy to-wazin-navy/90">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            {i18n.language === 'ar' 
              ? 'هل تحتاج إلى استشارة قانونية؟'
              : 'Need a Legal Consultation?'
            }
          </h2>
          <p className="text-xl text-white/90 mb-8">
            {i18n.language === 'ar' 
              ? 'فريقنا من الخبراء القانونيين جاهز لمساعدتك'
              : 'Our team of legal experts is ready to help you'
            }
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button
              onClick={() => navigate('/book-consultation')}
              className="bg-wazin-gold text-wazin-navy px-8 py-6 text-lg hover:shadow-lg transition-all gap-2 font-bold"
            >
              {i18n.language === 'ar' ? 'احجز استشارة' : 'Book Consultation'}
              {i18n.language === 'ar' ? (
                <ArrowLeft className="w-5 h-5" />
              ) : (
                <ArrowRight className="w-5 h-5" />
              )}
            </Button>
            <Button
              onClick={() => navigate('/#contact')}
              variant="outline"
              className="border-2 border-white text-white hover:bg-white hover:text-wazin-navy px-8 py-6 text-lg"
            >
              {i18n.language === 'ar' ? 'اتصل بنا' : 'Contact Us'}
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default DivisionsPage;
